// Step 5 — Import Control (docs/workflow.md §5, app.md §6). A single-status
// queue over invoices at `Pending Import Control Decision`, and since
// 2026-09-29 THE ONE EDITING STEP: every matched invoice lands here, whatever
// its company, and here the Company is settled, every field and line is
// corrected, every flag is resolved and the invoice is released to its
// company's approvers (owner — `docs/decisions.md` D-215 to D-220: *"let's make
// a singular Import Control the only step where flags can be resolved and
// fields can be edited"*).
//
// THIS FILE IS THE TWO OLD SCREENS MERGED. Import Control settled an ambiguous
// Company and nothing else (issue 3, 2026-08-11 — D-174, D-179); AP/AR
// Approval (`ApArApprovalScreen.tsx`, deleted with the merge) did the rest on a
// company-scoped `ap-ar` seat. What this screen is now is what AP/AR's was —
// the queue, the flag gate, the row buttons and bulk bar behind
// `BULK_ACTIONS_SHOWN`, the inline coding pickers, the modal's full editor —
// with the Company live in that editor and Import Control's own role, which
// covers every invoice, as the one authority. The flags pill is back on the
// card, because resolving flags is this step's work again (it was hidden here
// by owner item 14, 2026-08-31, while the step was Company-only).
//
// A matching failure (question 4.11, 2026-08-12) arrives here as an ORDINARY
// row carrying a `MatchingFailed` flag, and is resolved like any other flag.
//
// Two outcomes, no deny: `approved` IS the release — the company's approvers
// gain access — and `archived` matches a document that turns
// out not to be a payable at all. The modal's editor is the full surface:
// flags resolvable via Mark Resolved, amounts editable, every line's account
// and department editable and both REQUIRED at release (D-309). The read-only view (no
// `editStep`) with the disabled Approve/Hold/Archive bar and
// `NOT_AUTHORIZED_REASON` below should not normally be reachable, since the
// queue is filtered to what the caller can decide — it remains as the
// modal's own fallback.
//
// SELECTION AND PER-ROW BUTTONS (owner item 15, 2026-08-31), REVERSING owner
// item 5 of 2026-08-13 at AP/AR, and then HIDDEN by the owner on 2026-09-11
// (`BULK_ACTIONS_SHOWN` below). The flag gate answers the 2026-08-13 objection
// rather than removing the controls: the rows whose decision needed a reading
// are exactly the flagged ones, and those are unselectable and their buttons
// disabled. Do not re-remove the code without a new ruling.
import { DECISION_SOURCE, type DecisionSource } from '@ap/shared'
import { useMemo, useState } from 'react'
import {
  Button,
  Caption1,
  Toast,
  ToastBody,
  ToastTitle,
  makeStyles,
  tokens,
  useToastController,
} from '@fluentui/react-components'
import { CheckmarkRegular, DismissRegular } from '@fluentui/react-icons'
import { ScreenIntro } from '../components/ScreenIntro'
import type { Invoice } from '../lib/types'
import type { ReasonCategory } from '../lib/constants'
import {
  DecisionOutcome,
  DecisionStep,
  InvoiceStatus,
  ARCHIVE_CATEGORIES,
  SCREEN_SUBHEADER,
  TOASTER_ID,
} from '../lib/constants'
import { createDecision, createDecisionBatch, unresolvedFlagTypes } from '../lib/data'
import { errorMessage } from '../api/errors'
import { canDecide, canPreviewPdf, filterDecidable, type Session } from '../lib/identity'
import type { RefreshSignal } from '../lib/refresh'
import { useQueue, useSelection } from '../lib/hooks'
import { importControlReleaseSentence, resolveDecisionBatch, skippedNote } from '../lib/decisions'
import { InvoiceQueue } from '../components/InvoiceQueue'
import { InvoiceDetailModal } from '../components/InvoiceDetailModal'
import { DecisionDialog } from '../components/DecisionDialog'
import { applyFilters, type InvoiceFilter } from '../lib/filters'
import { actionA11y, heldReason, heldSelectionReason, useActiveHolds } from '../lib/holds'
import { ActionDivider } from '../components/ActionDivider'
import { HoldControl } from '../components/HoldControl'
import { useDecisionButtonStyles } from '../lib/theme'
import { Loading, EmptyQueue, ErrorBanner } from '../components/States'

const useStyles = makeStyles({
  modalActionButtons: { display: 'flex', alignItems: 'center', gap: '8px' },
  // NO `actionBar` / `count` (owner, 2026-09-15). The bulk buttons ride in the
  // queue's own toolbar beside Expand all now, so the strip they sat in — whose
  // left half had been empty by design since the count moved into the button on
  // 2026-09-14 — is gone, and `count` went with the count.
  // The flag gate's own caption, beside the buttons it disables. Amber rather
  // than red, same as the coding gate's on `ApprovalScreen`: nothing is wrong,
  // there is work left to do.
  gated: { color: tokens.colorPaletteDarkOrangeForeground1, maxWidth: '520px', textAlign: 'right' },
})

interface Props {
  session: Session
  /** The shell's refresh signal (D-160): the Refresh and Pipeline buttons and the 30-second timer. */
  refresh?: RefreshSignal
  /** Page-wide filter (header Filter button; also drives the timeline badges). */
  filter: InvoiceFilter
  /** Called when acted rows leave the queue, so the timeline badges refresh too. */
  onQueuesChanged?: () => void
}

const NOT_AUTHORIZED_REASON =
  'Only an Import Control holder can decide on an invoice at this step. You can view it, but your decision would be refused.'

/** What the bulk negative button says, and the one place it is written.
 *
 * NOT "Deny Selected", which is what the owner asked for by name (item 15,
 * 2026-08-31). There is deliberately no deny at this step: its two outcomes
 * are `approved` and `archived`, and the second is what a
 * reviewer reaches for when a document turns out not to be a payable. Labelling
 * it "Deny" would promise Denial Review and deliver an archive. The modal's own
 * button says **Archive** since 2026-09-15 (owner), so the bulk button says the
 * same thing about a selection. Both read "Not An Invoice" from owner item 5
 * (2026-08-12) until that date, when those words became one of the four
 * CATEGORIES the dialog offers.
 */
const BULK_ARCHIVE_LABEL = 'Archive Selected'

/**
 * EVERY ACT-WITHOUT-OPENING CONTROL ON THIS SCREEN, BEHIND ONE SWITCH (owner,
 * 2026-09-11, at AP/AR Approval: "let's hide the Select All, Approve Selected,
 * Mark Selected Not An Invoice, and the two per-invoice green and red buttons.
 * I think that they are more confusing than helpful here, but I may be asked
 * to put them back on the screen later").
 *
 * This screen's job is resolving flags, and a flag is resolved by opening the
 * invoice and looking at it. A green tick beside a row invites the one thing
 * the step is not for — approving without reading — and the bulk bar invites it
 * twenty rows at a time. The ruling came over with the rest of AP/AR's screen
 * when the two steps merged (D-225): the reason for it is the work this screen
 * now does.
 *
 * ONE BOOLEAN, NOT A DELETION, because the owner said they may be asked to put
 * it back. Flipping this to `true` restores all five controls exactly as they
 * were; nothing else has to be rebuilt, and the code behind them
 * (`openBulkApprove`, `openArchive`, `sel`, the blocked-reason plumbing) is
 * untouched and still covered by its tests. Every approval screen keeps its
 * own bulk bar — this is a ruling about this step, not about bulk actions.
 */
const BULK_ACTIONS_SHOWN: boolean = false

function selectedPhrase(n: number): string {
  return n === 1 ? 'the selected invoice' : `the ${n} selected invoices`
}

/** `source` is where the click was, stored on the decision (D-331). */
type Pending = { kind: 'approve' | 'archive'; ids: string[]; bulk: boolean; source: DecisionSource } | null

export function ImportControlScreen({ session, refresh, filter, onQueuesChanged }: Props) {
  const s = useStyles()
  const btn = useDecisionButtonStyles()
  const user = session.user
  // Declared ahead of `useQueue`: the queue is PAUSED while any of these is
  // open or in flight, so a background refresh never moves the gallery under a
  // modal or dialog; it catches up silently on close (D-160).
  const [openInvoice, setOpenInvoice] = useState<Invoice | null>(null)
  const [pending, setPending] = useState<Pending>(null)
  const [submitting, setSubmitting] = useState(false)
  const { invoices, loading, error, reload } = useQueue([InvoiceStatus.PendingImportControl], refresh, {
    paused: openInvoice !== null || pending !== null || submitting,
  })
  // Filtered to invoices the signed-in user can actually decide (owner
  // ruling, 2026-08-14): "If I have access to the invoice, then I should be
  // able to see and act on it. If I do not have access to the invoice, then it
  // would just never appear here." Import Control is a step ROLE covering
  // every invoice, so for a holder this filter drops nothing; it guards a
  // future change rather than closing a gap today.
  //
  // Kept as two steps rather than one combined filter so the empty state
  // below can tell "the page filter narrowed it to nothing" apart from
  // "none of these are yours to decide" instead of blaming the wrong one.
  // `filterDecidable` (lib/identity.ts) is the one function every decision
  // screen follows.
  const pageFiltered = useMemo(() => applyFilters(invoices, filter), [invoices, filter])
  const filtered = useMemo(
    () => filterDecidable(session, DecisionStep.ImportControl, pageFiltered),
    [pageFiltered, session],
  )
  // Keyed by id so the approve/archive actions below can re-resolve the
  // invoice they act on at the moment of submission, instead of sending the
  // `row_version` captured when the row was opened (B3) — this screen's
  // enablement gate (`openDecidable`) was never wrong, only the value handed
  // to the decision call could go stale and 409.
  const byId = useMemo(() => new Map(invoices.map((i) => [i.id, i])), [invoices])
  // THE OPEN INVOICE AS THE QUEUE NOW HAS IT (QA 2026-10-01, D1-05), as
  // `ApprovalScreen` and `ManualEntryScreen` already keep it. `openInvoice` is
  // the row as it was clicked and never changes, so a modal opened on a held
  // invoice kept the editor frozen after Release Hold until it was reopened.
  // Safe to pass to the modal: its effects and the editor's `key` are on the
  // id, so a fresher row re-renders it and does not remount it.
  const openInvoiceLive = openInvoice ? (byId.get(openInvoice.id) ?? openInvoice) : null

  // ---- The two gates ------------------------------------------------------
  //
  // THE FLAG GATE (owner item 15, 2026-08-31): "Similar to the way that
  // invoices 'on hold' are unable to be selected, only invoices without flags
  // should be able to be selected. The small, inline Approve and Deny buttons
  // too; just leave them disabled if the invoice has unresolved flags."
  //
  // For an APPROVE this mirrors the server exactly — `assertReleaseGates`
  // refuses an Import Control approval with `unresolved_flags` while the
  // count is above zero (D-218) — so the gate stops the
  // app offering an action that would be refused, the same shape D-9's coding
  // gate has on `ApprovalScreen`.
  //
  // For "NOT AN INVOICE" IT IS A DELIBERATE CLIENT-SIDE NARROWING, and the one
  // place to know it: the server EXEMPTS archive outcomes from those gates
  // (`routes/decisions.ts` — "archiving a document that was never payable must
  // not be blocked by the flags and sums of a document nobody will pay"), so a
  // flagged row could be archived in bulk if this let it. It does not, for two
  // reasons: the owner asked for one flag rule over both buttons, and an
  // archive is irreversible, so the 2026-08-13 objection — a reviewer must
  // read the document first — bites hardest exactly here. The escape hatch is
  // unchanged and one click away: Open the invoice and use the modal's own Not
  // An Invoice button, which is not gated.
  //
  // The count read is the row's own `unresolved_flag_count` — the same number
  // the flags pill on that card prints — so the rule a reviewer sees is
  // literally "a card showing flags cannot be picked".
  const unresolvedCount = (inv: Invoice): number =>
    inv.unresolved_flag_count ?? unresolvedFlagTypes(inv).length
  const flagReason = (inv: Invoice, where: 'queue' | 'bulk' = 'queue'): string | undefined => {
    const count = unresolvedCount(inv)
    if (count === 0) return undefined
    const subject = count === 1 ? 'One flag is still unresolved' : `${count} flags are still unresolved`
    return where === 'bulk'
      ? `${subject}.`
      : `${subject}. Open the invoice and resolve ${count === 1 ? 'it' : 'them'} before acting.`
  }
  const flaggedIds = useMemo(
    () => new Set(filtered.filter((i) => unresolvedCount(i) > 0).map((i) => i.id)),
    [filtered],
  )

  // Active holds for the loaded queue, keyed by id. A held invoice is stopped:
  // dimmed, unselectable, Approve/Archive disabled. Its fields stay editable
  // (owner, 2026-10-03 — D-282); until then its coding rendered read-only. Since 2026-09-25 (owner) this screen also PLACES and
  // RELEASES holds: the modal's Hold sits immediately left of Archive.
  const { holdFor, isHeld, heldIdsIn, setHoldFor } = useActiveHolds(invoices)
  // HELD BY THE ROW'S OWN FLAG TOO (owner, 2026-10-01, D-263): a row that says
  // held is held before its hold record arrives.
  const heldIds = useMemo(() => heldIdsIn(filtered), [filtered, heldIdsIn])

  // THE SELECTABLE SET. Held and flagged rows are excluded HERE, again in
  // `selectionDisabled` below, and a third time inside `submit` — the same
  // three-times-independent discipline `ApprovalScreen` keeps for holds, for
  // the same reason: each of the three answers a different question (what may
  // be picked, why this one may not, and what is still true at the click).
  const sel = useSelection(
    useMemo(
      () => filtered.filter((i) => !heldIds.has(i.id) && !flaggedIds.has(i.id)).map((i) => i.id),
      [filtered, heldIds, flaggedIds],
    ),
  )
  const selectedIds = [...sel.selected]

  // One source for one truth: the modal's editable-vs-read-only branch reads
  // this rather than computing `canDecide` on the open invoice a second time.
  // Filtering `filtered` above means every invoice reachable through this
  // screen's Open button is already decidable, so this should always be
  // true in practice — kept as the modal's own check rather than assumed,
  // since the client's `canDecide` hint is not the server's authority check.
  // On the click copy, deliberately: this picks the modal's editable or
  // read-only branch, which must not flip while the reviewer edits. Import
  // Control is a step role over every invoice, so no edit changes the answer.
  const openDecidable = !!openInvoice && canDecide(session, DecisionStep.ImportControl, openInvoice)
  /** WHAT THE DIALOG SAYS, held separately from WHETHER IT IS OPEN — the same
      split `ApprovalScreen` keeps. `pending` is nulled the moment a decision
      settles and Fluent keeps the surface mounted through its exit animation,
      so a dialog reading `pending` directly flashes the other action's wording
      on its way out. Set at open time and never cleared. */
  const [dialogContent, setDialogContent] = useState<Pending>(null)
  function openPending(next: NonNullable<Pending>) {
    setDialogContent(next)
    setPending(next)
  }
  const [actionError, setActionError] = useState<string | null>(null)
  const { dispatchToast } = useToastController(TOASTER_ID)

  function settle() {
    setPending(null)
    setOpenInvoice(null)
    sel.clear()
    // Silent (owner, 2026-09-28): a loud reload swaps the list for a
    // placeholder, the page shrinks, and the reviewer is thrown to the top.
    void reload({ silent: true })
    onQueuesChanged?.()
  }

  /**
   * ONE SUBMIT for both outcomes, one invoice or many — the shape
   * `ApprovalScreen` uses. One id goes to the single-decision endpoint, whose
   * refusal reason is the error message; two or more go to the batch endpoint,
   * which always resolves and reports per invoice what it refused and why.
   */
  async function submit(
    requestedIds: string[],
    outcome: typeof DecisionOutcome[keyof typeof DecisionOutcome],
    reason?: string,
    category?: string,
    /** Where the click was (D-331): the bar, the row, or the modal. */
    source?: DecisionSource,
  ) {
    setSubmitting(true)
    setActionError(null)
    const approved = outcome === DecisionOutcome.Approved
    // A toast is a SENTENCE, not a caption (owner, 2026-09-10). Composing the
    // count and the outcome together is what keeps the subject and the verb
    // agreeing; concatenating a count onto a bare participle did not.
    // A release names where each invoice went (D-237): its company's
    // approvers, or Export Control or Manual Entry for a company that staffs
    // no tier.
    const outcomeSentence = (releasedTo: readonly (string | null | undefined)[]): string => {
      const count = releasedTo.length
      const subject = count === 1 ? 'The invoice was' : `${count} invoices were`
      if (!approved) return `${subject} archived.`
      return importControlReleaseSentence(releasedTo)
    }
    // Re-resolve the batch NOW, not when the dialog opened: the queue reloads
    // behind an open dialog, so a row may have left it, gone on hold, gained a
    // flag, or fallen out of this user's authority since the selection was
    // snapshotted. The API would refuse those; filtering here keeps the
    // confirmation honest about what is actually being submitted
    // (lib/decisions.ts).
    const batch = resolveDecisionBatch(
      requestedIds,
      (id) => byId.get(id),
      (inv) => canDecide(session, DecisionStep.ImportControl, inv) && !isHeld(inv) && unresolvedCount(inv) === 0,
    )
    const acted = batch.eligible
    if (acted.length === 0) {
      setPending(null)
      setOpenInvoice(null)
      sel.clear()
      dispatchToast(
        <Toast>
          <ToastTitle>
            {batch.blocked.length === 0
              ? 'Those invoices were already processed, so nothing was submitted.'
              : 'Nothing was submitted, because those invoices are on hold, still flagged, or not yours to decide.'}
          </ToastTitle>
        </Toast>,
        { intent: 'info' },
      )
      setSubmitting(false)
      return
    }
    const skipped = skippedNote(batch)

    if (acted.length === 1) {
      try {
        const decided = await createDecision({
          invoice_id: acted[0].id,
          step: DecisionStep.ImportControl,
          decision: outcome,
          reason: reason || undefined,
          reason_category: (category as ReasonCategory) || undefined,
          row_version: acted[0].row_version,
          source,
        })
        settle()
        dispatchToast(
          <Toast>
            <ToastTitle>
              {outcomeSentence([decided?.new_status])}
            </ToastTitle>
            {skipped && <ToastBody>{skipped}</ToastBody>}
          </Toast>,
          { intent: 'success' },
        )
      } catch (e) {
        setActionError(errorMessage(e))
      } finally {
        setSubmitting(false)
      }
      return
    }

    try {
      const result = await createDecisionBatch({
        step: DecisionStep.ImportControl,
        decision: outcome,
        reason: reason || undefined,
        // ONE CATEGORY FOR THE WHOLE BATCH, exactly as the reason has always
        // been: `createDecisionBatch` takes one of each and writes it onto
        // every invoice it applies.
        reason_category: (category as ReasonCategory) || undefined,
        invoice_ids: acted.map((i) => i.id),
        source,
      })
      settle()
      const refusals = result.results.filter((r) => !r.applied)
      if (refusals.length > 0) {
        const named = refusals
          .map((r) => {
            const inv = byId.get(r.invoice_id)
            const label = inv?.external_invoice_id || inv?.vendor_name || r.invoice_id
            return `${label}: ${r.error?.message ?? 'refused'}`
          })
          .join('; ')
        setActionError(
          `${result.applied_count} of ${result.results.length} went through. Not applied — ${named}`,
        )
      }
      dispatchToast(
        <Toast>
          <ToastTitle>
            {outcomeSentence(result.results.filter((r) => r.applied).map((r) => r.new_status))}
          </ToastTitle>
          {skipped && <ToastBody>{skipped}</ToastBody>}
        </Toast>,
        { intent: result.rejected_count > 0 ? 'warning' : 'success' },
      )
    } catch (e) {
      setActionError(errorMessage(e))
    } finally {
      setSubmitting(false)
    }
  }

  /** Why this id may not be acted on, or undefined. FAILS CLOSED on a lookup
      miss: an id that resolves to nothing counts as blocked, so a stale render
      cannot slip a decision through. */
  function blockedReason(id: string): string | undefined {
    const inv = byId.get(id)
    if (!inv) return 'That invoice is no longer in this queue.'
    const hold = holdFor(inv)
    if (hold) return heldReason(hold)
    if (!canDecide(session, DecisionStep.ImportControl, inv)) return NOT_AUTHORIZED_REASON
    return flagReason(inv)
  }

  // Row approve commits without a confirmation — the warning is bulk-only,
  // exactly as on `ApprovalScreen`. The re-check is the belt to the buttons'
  // braces: a stale render must not submit a decision the server would refuse.
  function approveNow(ids: string[], source: DecisionSource) {
    if (ids.length === 0 || ids.some((id) => blockedReason(id))) return
    void submit(ids, DecisionOutcome.Approved, undefined, undefined, source)
  }
  function openBulkApprove(ids: string[]) {
    if (ids.length === 0 || ids.some((id) => blockedReason(id))) return
    openPending({ kind: 'approve', ids, bulk: true, source: DECISION_SOURCE.bulk_queue })
  }
  function openArchive(ids: string[], source: DecisionSource) {
    if (ids.length === 0 || ids.some((id) => blockedReason(id))) return
    openPending({ kind: 'archive', ids, bulk: source === DECISION_SOURCE.bulk_queue, source })
  }

  /** The read-only modal's accessible reasons (QA C2-04): a hold is the
      description, any other refusal is folded into the name. */
  function modalWhy(inv: Invoice, decidable: boolean): { held?: string; reason?: string } {
    const hold = holdFor(inv)
    if (hold) return { held: heldReason(hold) }
    return { reason: decidable ? blockedReason(inv.id) : NOT_AUTHORIZED_REASON }
  }

  /** The modal's Hold — the same control every step renders (owner, 2026-09-25). */
  function holdControlFor(inv: Invoice) {
    return (
      <HoldControl
        invoiceId={inv.id}
        hold={holdFor(inv)}
        decidable={canDecide(session, DecisionStep.ImportControl, inv)}
        notAuthorizedReason={NOT_AUTHORIZED_REASON}
        disabled={submitting}
        onError={setActionError}
        onChanged={(hold) => {
          setHoldFor(inv.id, hold)
          void reload({ silent: true })
          onQueuesChanged?.()
        }}
      />
    )
  }

  const selectedBlocked = selectedIds.filter((id) => blockedReason(id)).length
  const bulkBlockedReason =
    selectedBlocked === 0
      ? undefined
      : selectedBlocked === 1
        ? 'One selected invoice can no longer be acted on — it is on hold, newly flagged, or has left this queue. Refresh to re-check.'
        : `${selectedBlocked} selected invoices can no longer be acted on — they are on hold, newly flagged, or have left this queue. Refresh to re-check.`

  return (
    <div>
      <ScreenIntro>{SCREEN_SUBHEADER.ImportControl}</ScreenIntro>

      <ErrorBanner message={error || actionError} />

      {loading ? (
        <Loading label="Loading invoices…" />
      ) : filtered.length === 0 ? (
        <EmptyQueue
          text={
            pageFiltered.length > 0
              ? 'Nothing here is assigned to you right now.'
              : invoices.length > 0
                ? 'No invoices match the current filters.'
                : undefined
          }
        />
      ) : (
        <InvoiceQueue
          invoices={filtered}
          screen={DecisionStep.ImportControl}
          /* INLINE WITH EXPAND ALL, not a bar of their own (owner, 2026-09-15).
             They sat in an action bar above this queue whose left half was
             empty by design — the count moved into the button on 2026-09-14 —
             so the screen opened with a row of white space above the row
             carrying Select all. `BULK_ACTIONS_SHOWN` still hides them whole
             (owner, 2026-09-11), and still hides the checkboxes with them
             through `selectable` below. */
          toolbarActions={
            BULK_ACTIONS_SHOWN ? (
              <>
                {bulkBlockedReason && <Caption1 className={s.gated}>{bulkBlockedReason}</Caption1>}
                <Button
                  className={btn.deny}
                  icon={<DismissRegular />}
                  disabled={sel.count === 0 || submitting || selectedBlocked > 0}
                  title={bulkBlockedReason}
                  onClick={() => openArchive(selectedIds, DECISION_SOURCE.bulk_queue)}
                >
                  {BULK_ARCHIVE_LABEL}
                </Button>
                <Button
                  className={btn.approve}
                  icon={<CheckmarkRegular />}
                  disabled={sel.count === 0 || submitting || selectedBlocked > 0}
                  title={bulkBlockedReason}
                  onClick={() => openBulkApprove(selectedIds)}
                >
                  {sel.count === 0 ? 'Approve Selected' : `Approve ${sel.count} Selected`}
                </Button>
              </>
            ) : undefined
          }
          intakeBadge="flags"
          // `selectable` off takes the per-row checkboxes AND the Select all
          // control with it — `InvoiceQueue` renders both from this one prop —
          // so the switch above does not need a second hook for the checkbox.
          selectable={BULK_ACTIONS_SHOWN}
          selectedIds={sel.selected}
          onToggle={sel.toggle}
          onToggleAll={sel.toggleAll}
          allSelected={sel.allSelected}
          onOpen={setOpenInvoice}
          rowActions={!BULK_ACTIONS_SHOWN ? undefined : (inv) => {
            const hold = holdFor(inv)
            const heldTitle = hold ? heldReason(hold) : undefined
            const blocked = heldTitle ?? flagReason(inv)
            // THE REASON MUST REACH THE ACCESSIBLE NAME. Both buttons are
            // icon-only, so they need an `aria-label` — and an `aria-label`
            // WINS over `title`, so a flat label would leave a screen-reader
            // user with the name and none of the reason the sighted hover
            // gives (the same fix `ApprovalScreen`'s row buttons carry). A
            // HOLD is the description instead (QA C2-04, `actionA11y`).
            const why = { held: heldTitle, reason: flagReason(inv) }
            return (
              <>
                <Button
                  size="small"
                  className={btn.deny}
                  icon={<DismissRegular />}
                  title={blocked ?? 'Archive'}
                  {...actionA11y('Archive', why)}
                  disabled={submitting || !!blocked}
                  onClick={() => openArchive([inv.id], DECISION_SOURCE.queue_row)}
                />
                <Button
                  size="small"
                  className={btn.approve}
                  icon={<CheckmarkRegular />}
                  title={blocked ?? 'Approve'}
                  {...actionA11y('Approve', why)}
                  disabled={submitting || !!blocked}
                  onClick={() => approveNow([inv.id], DECISION_SOURCE.queue_row)}
                />
              </>
            )
          }}
          selectionDisabled={(inv) => {
            const hold = holdFor(inv)
            return hold ? heldSelectionReason(hold) : flagReason(inv)
          }}
          dimmedIds={heldIds}
          heldIds={heldIds}
          // OWNER ITEM 22 (2026-08-31) — the coding pickers inline in the
          // expanded card, at exactly the grant the modal's editor holds:
          // the Account and the Department, both required at release since
          // D-309, so an empty Department is red here as in the modal
          // (`REQUIRED_AT`). At AP/AR this passed the Account alone.
          editAccounts
          editDepartments
          onCodingChanged={() => void reload({ silent: true })}
          codingDisplay="halves"
        />
      )}

      <InvoiceDetailModal
        open={!!openInvoice}
        invoice={openInvoiceLive}
        showPdf={canPreviewPdf(session)}
        user={user}
        screen={DecisionStep.ImportControl}
        editStep={openDecidable ? DecisionStep.ImportControl : undefined}
        onChanged={() => void reload({ silent: true })}
        onClose={() => setOpenInvoice(null)}
        hold={holdFor(openInvoiceLive)}
        // The screen's own answer, read live (D1-05), as the tiers pass it (D2-01).
        holdControl={openInvoiceLive && holdControlFor(openInvoiceLive)}
        onReleased={(releasedTo, lateEditsUnsaved) => {
          settle()
          dispatchToast(
            <Toast>
              <ToastTitle>
                {importControlReleaseSentence([releasedTo])}
              </ToastTitle>
              {/* Typed while the release was out: the release went through
                  without it (code review of D-284). */}
              {lateEditsUnsaved && (
                <ToastBody>What you typed after pressing Release was not saved.</ToastBody>
              )}
            </Toast>,
            { intent: lateEditsUnsaved ? 'warning' : 'success' },
          )
        }}
        onArchived={() => {
          settle()
          dispatchToast(
            <Toast>
              <ToastTitle>The invoice was archived.</ToastTitle>
            </Toast>,
            { intent: 'success' },
          )
        }}
        actions={
          // Rendered by the modal on its READ-ONLY branch only — a decidable
          // reviewer gets the editable form's own Hold / Archive / Release bar
          // instead (`editStep` above). The queue filter above means every
          // invoice reachable here is decidable, so this disabled pair
          // should not normally show; it stays as the modal's own fallback
          // rather than assuming the queue filter and the server's authority
          // check can never disagree.
          //
          // GATED THROUGH `blockedReason`, exactly like the queue's own
          // buttons — the flag term included. Nothing here may be ENABLED
          // that `approveNow` / `openArchive` would then refuse in silence.
          //
          // That costs nothing real: a decidable reviewer never sees this
          // pair. They get the editable form's own action bar, whose Archive
          // is not flag-gated (`EditableInvoiceForm.tsx`) — that is
          // the ungated archive path for a flagged document, and it is the one
          // a reviewer here actually reaches.
          //
          // [HOLD] [ARCHIVE] | [APPROVE] — the editor's own order (owner,
          // 2026-09-29): Hold immediately left of Archive (2026-09-25), then
          // the divider, then the forward action. The negative button says
          // ARCHIVE, as it does everywhere else — it read "Not An Invoice"
          // here after every other copy had moved on.
          openInvoiceLive && (
            <div className={s.modalActionButtons}>
              {holdControlFor(openInvoiceLive)}
              {/* No icon on the modal's Archive (owner, 2026-09-30, D-245). */}
              <Button
                className={btn.deny}
                disabled={submitting || !openDecidable || !!blockedReason(openInvoiceLive.id)}
                title={openDecidable ? blockedReason(openInvoiceLive.id) : NOT_AUTHORIZED_REASON}
                {...actionA11y('Archive', modalWhy(openInvoiceLive, openDecidable))}
                onClick={() => openArchive([openInvoiceLive.id], DECISION_SOURCE.modal)}
              >
                Archive
              </Button>
              <ActionDivider />
              {/* No icon on the modal's Approve (owner, 2026-09-30, D-245). */}
              <Button
                className={btn.approve}
                disabled={submitting || !openDecidable || !!blockedReason(openInvoiceLive.id)}
                title={openDecidable ? blockedReason(openInvoiceLive.id) : NOT_AUTHORIZED_REASON}
                {...actionA11y('Approve', modalWhy(openInvoiceLive, openDecidable))}
                onClick={() => approveNow([openInvoiceLive.id], DECISION_SOURCE.modal)}
              >
                Approve
              </Button>
            </div>
          )
        }
      />

      <DecisionDialog
        open={!!pending}
        // ONE HEADER FOR EVERY IRREVERSIBLE CONFIRMATION (owner, 2026-09-14).
        // It was the opening button's own text on the archive arm (owner item
        // 5, 2026-08-12), which named the action a second time rather than
        // asking the question the dialog exists to ask.
        title="Are you sure?"
        body={
          !dialogContent
            ? undefined
            : dialogContent.kind === 'approve'
              ? `Approving will immediately release ${selectedPhrase(dialogContent.ids.length)} to ${dialogContent.ids.length === 1 ? "its company's" : "their companies'"} approvers. You cannot undo this action.`
              : dialogContent.bulk
                ? `${selectedPhrase(dialogContent.ids.length).replace(/^the /, 'The ')} will be archived. You cannot undo this action. Any reason you give is recorded on ${dialogContent.ids.length === 1 ? 'the invoice' : 'every selected invoice'} and visible in the decision history.`
                : 'This document will be archived. Any reason you give is recorded on the record and visible in the decision history.'
        }
        /* ONE REASON FOR THE WHOLE BATCH. `createDecisionBatch` takes a single
           `reason` and writes it onto every invoice it applies, so the dialog
           asks once — there is no per-invoice reason to collect and nowhere to
           put one. The body above says so, in the bulk arm. */
        requireReason={dialogContent?.kind === 'archive'}
        /* THE NOT AN INVOICE VOCABULARY, required (owner, 2026-09-15) — the
           same four the single-invoice Archive dialog offers, so the
           bulk arm and the one-at-a-time arm ask the same question. Absent on
           the approve arm, which takes no category. */
        categories={dialogContent?.kind === 'archive' ? ARCHIVE_CATEGORIES : undefined}
        reasonLabel="Comment"
        /* AN EXAMPLE, NOT A DESCRIPTION OF THE CONTROL (owner, 2026-09-14) —
           the same sentence the single-invoice Archive dialog shows, so
           the bulk arm and the one-at-a-time arm ask the same way. */
        reasonPlaceholder="This document is a statement. There is nothing to be paid."
        confirmLabel={dialogContent?.kind === 'approve' ? 'Approve' : 'Archive'}
        confirmClassName={dialogContent?.kind === 'approve' ? btn.approve : undefined}
        destructive={dialogContent?.kind === 'archive'}
        submitting={submitting}
        onCancel={() => setPending(null)}
        onConfirm={(reason, category) =>
          pending &&
          void submit(
            pending.ids,
            pending.kind === 'approve' ? DecisionOutcome.Approved : DecisionOutcome.Archived,
            reason,
            category,
            pending.source,
          )
        }
      />
    </div>
  )
}
